import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { firebaseConfig, firebaseVapidKey } from "../firebase/config";
import { registerNotificationDevice } from "../api/NotificationAPIs";

const ENABLED_KEY = "girabat-push-enabled";
const DEVICE_KEY = "girabat-device-id";

function app() {
  return getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
}

function deviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = window.crypto?.randomUUID?.() || `web-${Date.now()}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

function browserName() {
  const agent = navigator.userAgent || "";
  if (agent.includes("Edg/")) return "Edge";
  if (agent.includes("Chrome/")) return "Chrome";
  if (agent.includes("Firefox/")) return "Firefox";
  if (agent.includes("Safari/")) return "Safari";
  return "Browser";
}

async function messagingInstance() {
  if (!(await isSupported())) return null;
  return getMessaging(app());
}

async function currentToken(messaging) {
  if (!firebaseVapidKey) {
    throw new Error("missing-vapid");
  }
  const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  return getToken(messaging, {
    vapidKey: firebaseVapidKey,
    serviceWorkerRegistration: registration,
  });
}

export function pushEnabledLocally() {
  return localStorage.getItem(ENABLED_KEY) === "1";
}

export async function enableOrderNotifications() {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    return { status: "unsupported" };
  }
  if (Notification.permission === "denied") {
    return { status: "denied" };
  }
  const messaging = await messagingInstance();
  if (!messaging) return { status: "unsupported" };

  const permission =
    Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") {
    return { status: permission === "denied" ? "denied" : "default" };
  }

  try {
    const token = await currentToken(messaging);
    if (!token) return { status: "unavailable" };
    await registerNotificationDevice({
      fcmToken: token,
      deviceId: deviceId(),
      platform: navigator.platform || "web",
      browser: browserName(),
      deviceName: `${browserName()} على ${navigator.platform || "web"}`,
    });
    localStorage.setItem(ENABLED_KEY, "1");
    return { status: "granted" };
  } catch (error) {
    if (error?.message === "missing-vapid") return { status: "missing-vapid" };
    throw error;
  }
}

export async function refreshPushTokenIfEnabled() {
  if (!pushEnabledLocally()) return;
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const messaging = await messagingInstance();
  if (!messaging) return;
  try {
    const token = await currentToken(messaging);
    if (!token) return;
    await registerNotificationDevice({
      fcmToken: token,
      deviceId: deviceId(),
      platform: navigator.platform || "web",
      browser: browserName(),
      deviceName: `${browserName()} على ${navigator.platform || "web"}`,
    });
  } catch {
    // A missing VAPID key or a closed push service should not block the admin panel.
  }
}

export async function listenForForegroundMessages(onPayload) {
  const messaging = await messagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, onPayload);
}
